import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Card, CardContent, Typography, TextField, Grid, IconButton, Box, Autocomplete, FormHelperText, InputAdornment } from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';
import api from '../../../axios/api';
import { showNotification } from '../../../redux/slices/notificationSlice';
import { useDispatch } from 'react-redux';
import CustomButton from '../../../componentsII/Buttons';

const createUserSchema = yup.object().shape({
  firstName: yup
    .string()
    .min(2, 'First Name must be at least 2 characters')
    .max(50, 'First Name must be less than 50 characters')
    .required('First Name is required'),
  lastName: yup
    .string()
    .min(2, 'Last Name must be at least 2 characters')
    .max(50, 'Last Name must be less than 50 characters')
    .required('Last Name is required'),
  email: yup
    .string()
    .email('Invalid email address')
    .required('Email is required'),
  countryCode: yup
    .string()
    .required('Country Code is required'),
  phoneNo: yup
    .string()
    .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits')
    .required('Phone number is required'),
  password: yup.string().when('isUpdate', {
    is: false,
    then: () => yup
      .string()
      .min(8, 'Password must be at least 8 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        'Password must include uppercase, lowercase, number, and special character'
      )
      .required('Password is required'),
    otherwise: () => yup.string()
  }),
  confirmPassword: yup.string().when('password', {
    is: val => val && val.length > 0,
    then: () => yup
      .string()
      .oneOf([yup.ref('password')], 'Passwords do not match')
      .required('Confirm Password is required'),
    otherwise: () => yup.string()
  })
});

const AddUpdateUserModal = ({ defaultValues = null, onClose }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const dispatch = useDispatch();
  const isUpdate = !!defaultValues;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(createUserSchema),
    defaultValues: {
      firstName: defaultValues?.firstName || '',
      lastName: defaultValues?.lastName || '',
      email: defaultValues?.email || '',
      phoneNo: defaultValues?.phoneNo || '',
      countryCode: defaultValues?.countryCode || '',
      isUpdate: isUpdate
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get("/common/countryDrop");
        const formattedCountries = response.data.data.map((item) => ({
          key: item.value,
          value: item.telephone_code,
          label: `(${item.telephone_code}) ${item.country_code}`
        }));
        setCountries(formattedCountries);
        
        if (defaultValues?.countryCode) {
          const defaultCountry = formattedCountries.find(
            country => country.key === defaultValues.countryCode
          );
          setSelectedCountry(defaultCountry);
          setValue('countryCode', defaultCountry?.value);
        } else if (formattedCountries.length > 0) {
          setSelectedCountry(formattedCountries[0]);
          setValue('countryCode', formattedCountries[0].value);
        }
      } catch (error) {
        dispatch(showNotification({
          message: 'Failed to load country data',
          type: "error",
        }));
      }
    };

    fetchData();
  }, [setValue, defaultValues]);

  const onSubmit = async (data) => {
    try {
      const selectedCountry = countries.find(
        country => country.value === data.countryCode
      );
      
      const formData = {
        ...data,
        countryId: selectedCountry?.key
      };

      // Remove password fields if updating
      if (isUpdate && !data.password) {
        delete formData.password;
        delete formData.confirmPassword;
      }

      // Add ID if updating
      if (isUpdate) {
        formData.id = defaultValues.id;
      }
      
      const response = await api.post('/admin/CreateUpdateUser', formData);
      
      if (response.data.status === "200") {
        dispatch(showNotification({
          message: isUpdate ? 'User updated successfully' : 'User created successfully',
          type: "success",
        }));
        if (onClose) {
          onClose();
        }
      } else {
        dispatch(showNotification({
          message: isUpdate ? 'Failed updating user' : 'Failed creating user',
          type: "error",
        }));
      }
    } catch (error) {
      dispatch(showNotification({
        message: 'An error occurred',
        type: "error",
      }));
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <CardContent>
        {/* <Typography variant="body2" color="text.secondary" gutterBottom>
          {isUpdate ? 'Update user details' : 'Fill out the details to get started'}
        </Typography> */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={<span>First Name<span style={{ color: 'red' }}> *</span></span>}
                {...register('firstName')}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={<span>Last Name<span style={{ color: 'red' }}> *</span></span>}
                {...register('lastName')}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={<span>Email Address<span style={{ color: 'red' }}> *</span></span>}
                type="email"
                {...register('email')}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={countries}
                value={selectedCountry}
                onChange={(_, newValue) => {
                  setSelectedCountry(newValue);
                  setValue('countryCode', newValue?.value || '');
                }}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<span>Select Country<span style={{ color: 'red' }}>*</span></span>}
                    error={!!errors.countryCode}
                    helperText={errors.countryCode?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={<span>Phone Number<span style={{ color: 'red' }}> *</span></span>}
                type="number"
                {...register('phoneNo')}
                error={!!errors.phoneNo}
                helperText={errors.phoneNo?.message}
              />
            </Grid>

            {(!isUpdate || (isUpdate && defaultValues?.showPasswordFields)) && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={<span>Password{!isUpdate && <span style={{ color: 'red' }}> *</span>}</span>}
                    type={showPassword ? 'text' : 'password'}
                    {...register('password')}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={<span>Confirm Password{!isUpdate && <span style={{ color: 'red' }}> *</span>}</span>}
                    type={showPassword ? 'text' : 'password'}
                    {...register('confirmPassword')}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormHelperText>
                    At least 8 characters, including uppercase, lowercase, number, and special character
                  </FormHelperText>
                </Grid>
              </>
            )}
          </Grid>
          <div className="flex justify-end gap-5 items-center mt-5">
            <CustomButton
              className="bg-gray-500 hover:bg-gray-700 text-white"
              text="Cancel"
              click={onClose}
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white"
              text={isUpdate ? "Update" : "Submit"}
              type="submit"
            />
          </div>
        </form>
      </CardContent>
    </Box>
  );
};

export default AddUpdateUserModal;